import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Path from '@leverege/path';
import { R } from '@leverege/value-resolver';
import Theme from "./Theme.js";
import S from './DataBlock.css';

/** 
 * renders a column or row seperated title and data format 
 * @property {boolean} vertical - determines if the block has a vertical layout or horizontal layout 
 * @property {array} fields - the fields to be rendered or read from the item and rendered each element is an object 
 * @property {object} item - the data item that is to be rendered by the data block 
 * @property {string|node} title - text or element to be the block  
 * @property {string} className - css class to be applied to this component 
 * @property {string} variant - Theme variant name to be added to the component 
 * */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class DataBlock extends React.Component {
  static propTypes = {
    vertical: PropTypes.bool,
    // eslint-disable-line
    fields: PropTypes.array.isRequired,
    // eslint-disable-line
    item: PropTypes.object,
    // eslint-disable-line
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    // eslint-disable-line
    className: PropTypes.string,
    // eslint-disable-line
    variant: PropTypes.string // eslint-disable-line
  };
  static defaultProps = {
    item: null
  };
  static contextType = Theme.Context;
  render() {
    const theme = this.context;
    const {
      vertical = true,
      fields,
      item,
      title,
      className,
      variant
    } = theme.props('DataBlock', this.props);
    const paneCn = theme.classes('DataBlock', 'dataBlock', variant, className);
    const titleCn = theme.subclasses('DataBlock', 'title');
    const fieldsCn = vertical ? S.vertical : S.horizontal;
    const oddCn = theme.subclasses('DataBlock', 'odd');
    const evenCn = theme.subclasses('DataBlock', 'even');
    const rowCn = theme.subclasses('DataBlock', 'row', S.row);
    const labelCn = theme.subclasses('DataBlock', 'label');
    const valueCn = theme.subclasses('DataBlock', 'value');
    const suffixCn = theme.subclasses('DataBlock', 'suffix');
    let oddRow = true;
    return /*#__PURE__*/_jsxs("div", {
      className: paneCn,
      children: [title != null && /*#__PURE__*/_jsx("div", {
        className: titleCn,
        children: title
      }), /*#__PURE__*/_jsx("div", {
        className: fieldsCn,
        children: fields.map((field, idx) => {
          /**
           * the following keys are for each item of fields. if no accessor is provided, an a value is, the value will be directly rendered
           * @param {string|function} name the label of the field
           * @param {string|function} accessor function( item ) and returns the value. If this
           * is a string it will traverse a path to get the value. eg. "values/temp" will return 
           * item.values.temp
           * @param {string|function} suffix the suffix for the value
           * @param {string} rowClassName a classname to add to the row
           * @param {string|function} nameClassName a classname to add to the name. If this
           * is a function, it will be given ( value, item, field )
           * @param {string|function} valueClassName a classname to add to the value. If this
           * is a function, it will be given ( value, item, field )
           * @param {string|function} suffixClassName a classname to add to the suffix.If this
           * is a function, it will be given ( value, item, field )
           **/
          const {
            name = '',
            accessor,
            value,
            suffix,
            rowClassName,
            nameClassName,
            valueClassName,
            suffixClassName
          } = field;
          let val = value;
          let suf = suffix;
          const aType = typeof accessor;
          if (aType === 'string') {
            val = getRenderable(Path.get(accessor, item, null));
          } else if (aType === 'function') {
            val = getRenderable(accessor(item));
          }
          if (typeof suffix === 'function') {
            suf = getRenderable(suffix(value, item, field));
          }
          const vcn = resolveCn(valueClassName, value, item, field);
          const ncn = resolveCn(nameClassName, value, item, field);
          const scn = resolveCn(suffixClassName, value, item, field);
          oddRow = !oddRow;
          return (
            /*#__PURE__*/
            // eslint-disable-next-line
            _jsxs("div", {
              className: classNames(rowCn, rowClassName, oddRow ? oddCn : evenCn),
              children: [/*#__PURE__*/_jsx("div", {
                className: classNames(labelCn, ncn),
                children: R(name)
              }), /*#__PURE__*/_jsxs("div", {
                className: classNames(valueCn, vcn),
                children: [val, suf && /*#__PURE__*/_jsx("span", {
                  className: classNames(suffixCn, scn),
                  children: suf
                })]
              })]
            }, idx)
          );
        })
      })]
    });
  }
}

/**
 * Determines if given value is something that react can render.
 * @param {any} val value to check
 * @returns {boolean} true iff rendering this val will not crash React
 */
function isRenderable(val) {
  return val === null || /*#__PURE__*/React.isValidElement(val) || typeof val !== 'object' && typeof val !== 'function';
}

/**
 * If val is renderable, returns val. If not, returns dVal (or null if dVal not renderable).
 * @param {any} val value to check
 * @param {string|React.component|null} [dVal] what to return if val fails check
 */
function getRenderable(val, dVal = null) {
  return isRenderable(val) ? val : getRenderable(dVal);
}
function resolveCn(cn, value, item, field) {
  if (typeof cn === 'function') {
    return cn(value, item, field);
  }
  return cn;
}
export default DataBlock;